import { Middleware, isFulfilled, isRejectedWithValue } from "@reduxjs/toolkit";
import { emitApiResult } from "./apiResultEvents";
import { getApiRequestMeta } from "./utils";

type RtkQueryMetaArg = {
  endpointName?: string;
  type?: "query" | "mutation";
  originalArgs?: unknown;
};

const getRtkQueryMetaArg = (action: {
  meta?: { arg?: unknown };
}): RtkQueryMetaArg | undefined => {
  const arg = action.meta?.arg;
  if (
    !arg ||
    typeof arg !== "object" ||
    !("endpointName" in arg) ||
    typeof (arg as RtkQueryMetaArg).endpointName !== "string"
  ) {
    return undefined;
  }
  return arg as RtkQueryMetaArg;
};

/**
 * Global tap for RTK Query results (errors + optional success toasts).
 * Emits to `apiResultEvents`; the ApiResultHandlingProvider subscribes and
 * runs the UI pipeline. Add to each app's store.
 */
export const apiResultMiddleware: Middleware = () => (next) => (action) => {
  // Emit after the reducer so subscribers observe the settled RTK Query state.
  const result = next(action);

  if (isRejectedWithValue(action) || isFulfilled(action)) {
    const arg = getRtkQueryMetaArg(action);

    if (!arg) {
      return result;
    }

    const kind = arg.type === "mutation" ? "mutation" : "query";
    const endpointName = arg.endpointName ?? "";
    const meta = getApiRequestMeta(arg.originalArgs);

    if (isRejectedWithValue(action)) {
      emitApiResult({
        type: "error",
        rawError: action.payload,
        endpointName,
        kind,
        meta,
      });
    } else if (meta?.successMessage) {
      emitApiResult({
        type: "success",
        endpointName,
        kind,
        successMessage: meta.successMessage,
      });
    }
  }

  return result;
};
